import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import '../styles/PaymentForm.css';

// Load Stripe outside of components to avoid recreating on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentFormContent = ({ onSuccess, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        if (!stripe || !elements) {
            setError('Stripe not loaded');
            setLoading(false);
            return;
        }

        try {
            // Create setup intent
            const response = await fetch('/api/payment/setup-intent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    provider: 'stripe'
                })
            });

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.message || 'Failed to create setup intent');
            }

            // Confirm the setup intent
            const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
                data.client_secret,
                {
                    payment_method: {
                        card: elements.getElement(CardElement),
                        billing_details: {
                            name: 'Card Holder Name',
                        },
                    }
                }
            );

            if (confirmError) {
                throw new Error(confirmError.message);
            }

            if (setupIntent.status === 'succeeded') {
                // Call backend to complete the setup intent and save payment method
                const completeResponse = await fetch('/api/payment/complete-setup-intent', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify({
                        setup_intent_id: setupIntent.id,
                        provider: 'stripe'
                    })
                });

                const completeData = await completeResponse.json();

                if (!completeData.success) {
                    throw new Error(completeData.message || 'Failed to save payment method');
                }

                onSuccess(completeData.data);
            } else {
                throw new Error('Setup intent failed');
            }

        } catch (err) {
            setError(err.message);
            onError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="payment-form">
            <div className="card-element-container">
                <label htmlFor="card-element">Dettagli della carta</label>
                <CardElement
                    id="card-element"
                    options={{
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#424770',
                                '::placeholder': {
                                    color: '#aab7c4',
                                },
                            },
                            invalid: {
                                color: '#9e2146',
                            },
                        },
                    }}
                />
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            <button 
                type="submit" 
                disabled={!stripe || loading}
                className="submit-button"
            >
                {loading ? 'Salvataggio...' : 'Salva Metodo di Pagamento'}
            </button>
        </form>
    );
};

const PaymentForm = ({ onSuccess, onError }) => {
    return (
        <Elements stripe={stripePromise}>
            <PaymentFormContent onSuccess={onSuccess} onError={onError} />
        </Elements>
    );
};

export default PaymentForm;
